/**
 * Navigation Header v2
 * 
 * Features:
 * - Desktop dropdown navigation with hover/focus states
 * - Mobile drawer navigation with tabs
 * - Predictive search integration
 * - Z-index management for overlays
 * - Transparent navigation mode support
 * - Two-step mobile search close behavior
 * - Currency selector integration
 */

class NavDrawer extends HTMLElement {
  constructor() {
    super();
    
    // State
    this.isDesktop = window.innerWidth >= 960;
    this.activePanel = null;
    this.activeTab = null;
    this.collapseTimeout = null;
    this.panelResetTimeouts = new Map(); // Track panel position reset timeouts
    this.isSearchFocused = false;
    this.initialized = false;
    
    // Bind methods
    this.handleResize = this.debounce(this.handleResize.bind(this), 250);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleSearchInput = this.debounce(this.handleSearchInput.bind(this), 300);
    // Use requestAnimationFrame for smoother scroll detection
    this.scrollRafId = null;
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Core elements - query after DOM is ready
    this.header = document.querySelector('.nav__root');
    this.headerSection = document.getElementById('shopify-section-header');
    this.overlay = document.getElementById('overlay');
    this.menuToggle = document.getElementById('menu-toggle');
    this.container = this.querySelector('.nav-drawer__container');
    
    // Desktop navigation
    this.desktopNav = document.querySelector('.nav__desktop');
    this.menuLinks = document.querySelectorAll('.nav__menu-link[data-panel]');
    this.panels = document.querySelectorAll('.nav__panel');
    
    // Mobile elements
    this.tabs = this.querySelectorAll('.nav-drawer__tab');
    this.tabPanels = this.querySelectorAll('.nav-drawer__panel');
    
    // Search elements
    this.searchInput = this.querySelector('[data-mobile-search-input]');
    this.searchClose = this.querySelector('[data-search-close]');
    this.searchResults = this.querySelector('[data-search-results]');
    
    // Event listeners
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }
    
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.toggleMobileMenu());
    }
    
    // Initialize based on screen size
    this.setupNavigation();
    this.setupMobileDrawer();
    this.setupSearch();
    this.setupCurrencySelectors();
    this.setupOverlayMonitoring();
    this.setupTransparentHover();
    
    // Check initial sticky state
    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.onScroll);
    this.cleanupDesktopListeners();
    
    // Cancel any pending scroll RAF
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
    
    // Cleanup transparent hover listeners
    if (this.header && this.handleNavMouseEnter && this.handleNavMouseLeave) {
      this.header.removeEventListener('mouseenter', this.handleNavMouseEnter);
      this.header.removeEventListener('mouseleave', this.handleNavMouseLeave);
    }
  }

  // ==================
  // Utility Methods
  // ==================

  // Use global debounce function from global.js (bound to this context)
  debounce(func, delay) {
    return debounce(func.bind(this), delay);
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 960;
    
    if (wasDesktop !== this.isDesktop) {
      this.cleanupDesktopListeners();
      this.setupNavigation();
      this.resetState();
    }
    
    // Re-check sticky state after resize
    this.handleScroll();
  }

  // Optimized scroll handler using requestAnimationFrame
  // Always schedule latest scroll position, canceling previous if pending
  onScroll = () => {
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
    }
    this.scrollRafId = requestAnimationFrame(() => {
      this.handleScroll();
      this.scrollRafId = null;
    });
  }

  handleScroll() {
    if (!this.headerSection || !this.header) return;
    
    // Check if the header section is stuck (top of element is at or above viewport top)
    const rect = this.headerSection.getBoundingClientRect();
    const isStuck = rect.top <= 0;
    
    if (isStuck) {
      this.header.classList.add('nav__stuck');
    } else {
      this.header.classList.remove('nav__stuck');
    }
    
    // Handle transparent mode scroll behavior
    const isTransparent = this.header.dataset.transparent === 'true';
    if (isTransparent) {
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollThreshold = 10;
      
      if (scrollY > scrollThreshold) {
        this.header.classList.add('nav__scrolled');
      } else {
        this.header.classList.remove('nav__scrolled');
      }
    }
  }

  resetState() {
    this.closeMobileMenu();
    this.closeAllPanels();
    this.activePanel = null;
    this.activeTab = null;
    clearTimeout(this.collapseTimeout);
  }

  // ==================
  // Desktop Navigation
  // ==================

  setupNavigation() {
    if (!this.isDesktop) {
      this.cleanupDesktopListeners();
      return;
    }

    // Track hover state for the entire nav area
    this.isHoveringNav = false;

    this.menuLinks.forEach(link => {
      const panelId = link.dataset.panel;
      const panel = document.getElementById(panelId);
      
      if (!panel) return;

      // Mouse events with hover tracking
      const expandHandler = () => {
        this.isHoveringNav = true;
        this.expandPanel(panel, link);
      };
      
      const collapseHandler = (e) => {
        // Check if we're moving to the panel or another menu link
        const relatedTarget = e.relatedTarget;
        
        if (relatedTarget) {
          // If moving to the panel itself, don't collapse
          if (panel.contains(relatedTarget) || relatedTarget === panel) {
            return;
          }
          // If moving to another menu link, let that link's handler manage it
          if (relatedTarget.classList?.contains('nav__menu-link')) {
            return;
          }
          // If moving to something inside the nav panels area
          if (relatedTarget.closest('.nav__panels') || relatedTarget.closest('.nav__panel')) {
            return;
          }
        }
        this.collapsePanel(panel, link);
      };

      const panelEnterHandler = () => {
        this.isHoveringNav = true;
        clearTimeout(this.collapseTimeout);
      };

      const panelLeaveHandler = (e) => {
        // Check if we're moving back to the link or another panel element
        const relatedTarget = e.relatedTarget;
        if (relatedTarget) {
          if (link.contains(relatedTarget) || relatedTarget === link) {
            return;
          }
          if (relatedTarget.classList?.contains('nav__menu-link')) {
            return;
          }
          if (relatedTarget.closest('.nav__panels') || relatedTarget.closest('.nav__panel')) {
            return;
          }
        }
        this.collapsePanel(panel, link);
      };
      
      link.addEventListener('mouseenter', expandHandler);
      link.addEventListener('mouseleave', collapseHandler);
      link.addEventListener('focus', expandHandler);
      link.addEventListener('blur', () => this.collapsePanel(panel, link));
      
      panel.addEventListener('mouseenter', panelEnterHandler);
      panel.addEventListener('mouseleave', panelLeaveHandler);
      
      // Store for cleanup
      link._navHandlers = { expand: expandHandler, collapse: collapseHandler };
      panel._navHandlers = { expand: panelEnterHandler, collapse: panelLeaveHandler };
    });
  }

  cleanupDesktopListeners() {
    this.menuLinks.forEach(link => {
      if (link._navHandlers) {
        link.removeEventListener('mouseenter', link._navHandlers.expand);
        link.removeEventListener('mouseleave', link._navHandlers.collapse);
        link.removeEventListener('focus', link._navHandlers.expand);
        link.removeEventListener('blur', link._navHandlers.collapse);
        delete link._navHandlers;
      }
      
      const panelId = link.dataset.panel;
      const panel = document.getElementById(panelId);
      if (panel && panel._navHandlers) {
        panel.removeEventListener('mouseenter', panel._navHandlers.expand);
        panel.removeEventListener('mouseleave', panel._navHandlers.collapse);
        delete panel._navHandlers;
      }
    });
  }

  expandPanel(panel, link) {
    // Don't expand if cart/quickview/filters are open
    if (this.isOverlayActive()) return;
    
    clearTimeout(this.collapseTimeout);
    
    // Cancel any pending position reset for this panel
    if (this.panelResetTimeouts.has(panel)) {
      clearTimeout(this.panelResetTimeouts.get(panel));
      this.panelResetTimeouts.delete(panel);
    }
    
    // Close previous panel if different
    if (this.activePanel && this.activePanel !== panel) {
      this.activePanel.classList.remove('active');
      if (this.activeTab) {
        this.activeTab.setAttribute('aria-expanded', 'false');
      }
    }
    
    const wasAnyDesktopPanelOpen = !!this.activePanel;

    // Open new panel
    panel.classList.add('active');
    link.setAttribute('aria-expanded', 'true');
    this.overlay.classList.add('active');
    this.header.classList.add('nav__in-front');
    if (!wasAnyDesktopPanelOpen) {
      lockBodyScroll();
    }
    
    // Dynamically set overlay and panel top to start below the nav bar
    const isTransparent = this.header.dataset.transparent === 'true';
    const isScrolled = this.header.classList.contains('nav__scrolled');
    let topPosition;
    
    if (isTransparent) {
      // In transparent mode: header is absolutely positioned
      // Always use header's getBoundingClientRect() because headerSection has zero height
      // when header is absolutely positioned inside it
      const headerRect = this.header.getBoundingClientRect();
      if (isScrolled) {
        // When scrolled: header is at top: 0, use bottom directly
        topPosition = `${headerRect.bottom}px`;
      } else {
        // At top: header may have offset from other content, use height only
        // Since header is absolutely positioned, its height is the correct position
        topPosition = `${headerRect.height}px`;
      }
    } else {
      // Regular pages: use headerSection for accurate sticky positioning
      const navRect = this.headerSection ? this.headerSection.getBoundingClientRect() : this.header.getBoundingClientRect();
      topPosition = `${navRect.bottom}px`;
    }
    
    this.overlay.style.top = topPosition;
    panel.style.top = topPosition;
    
    this.activePanel = panel;
    this.activeTab = link;
  }

  collapsePanel(panel, link) {
    // Use timeout to prevent flickering when moving between elements
    this.collapseTimeout = setTimeout(() => {
      if (this.activePanel === panel) {
        this.closeAllPanels();
      }
    }, 150);
  }

  closeAllPanels() {
    clearTimeout(this.collapseTimeout);
    const hadDesktopPanelOpen = !!this.activePanel;
    
    this.panels.forEach(panel => {
      panel.classList.remove('active');
      // Delay resetting top position until after fade transition completes
      // Store timeout ID so it can be cancelled if panel is re-expanded quickly
      const timeoutId = setTimeout(() => {
        panel.style.top = ''; // Reset to CSS default
        this.panelResetTimeouts.delete(panel);
      }, 200); // Match the panel transition duration (0.2s)
      this.panelResetTimeouts.set(panel, timeoutId);
    });
    this.menuLinks.forEach(link => link.setAttribute('aria-expanded', 'false'));
    
    if (hadDesktopPanelOpen && !this.isMobileMenuOpen()) {
      this.overlay.classList.remove('active');
      this.header.classList.remove('nav__in-front');
      unlockBodyScroll();
      // Don't reset overlay position - let it stay at the current position.
      // The inline style will be updated the next time expandPanel is called anyway.
      // This prevents a visible position jump when the CSS rule applies.
    }
    
    this.activePanel = null;
    this.activeTab = null;
  }

  // ==================
  // Mobile Navigation
  // ==================

  setupMobileDrawer() {
    // Tab switching - only for button tabs, not direct link tabs
    this.tabs.forEach(tab => {
      // Skip direct link tabs (they're <a> elements that navigate)
      if (tab.tagName === 'A' || tab.hasAttribute('data-direct-link')) {
        return;
      }
      tab.addEventListener('click', () => {
        const href = tab.dataset.href;
        if (tab.classList.contains('active') && href) {
          window.location.href = href;
        } else {
          this.switchTab(tab);
        }
      });
    });
  }

  toggleMobileMenu() {
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  isMobileMenuOpen() {
    return this.menuToggle?.getAttribute('aria-expanded') === 'true';
  }

  openMobileMenu() {
    const wasOpen = this.isMobileMenuOpen();

    // Close cart if open
    try {
      window.closeCart?.();
    } catch {}

    setAriaExpanded(this.menuToggle, true);
    this.header.classList.add('nav__mobile-menu-open', 'nav__in-front');
    if (this.overlay) {
      this.overlay.style.top = ''; // Clear any inline top from desktop panels
      this.overlay.classList.add('active');
    }
    if (!wasOpen) {
      lockBodyScroll();
    }

    // Activate first tab if none active
    if (!this.querySelector('.nav-drawer__tab.active')) {
      const firstTab = this.tabs[0];
      if (firstTab) this.switchTab(firstTab);
    }

    // Focus trap
    if (this.container && typeof trapFocus === 'function') {
      trapFocus(this.container);
    }
    this.updateZIndex();
  }

  closeMobileMenu() {
    const wasOpen = this.isMobileMenuOpen();

    setAriaExpanded(this.menuToggle, false);
    setAriaExpanded(document.getElementById('search-toggle-mobile'), false);
    this.header?.classList.remove('nav__mobile-menu-open');

    if (!this.activePanel) {
      this.overlay?.classList.remove('active');
      this.header?.classList.remove('nav__in-front');
    }

    if (wasOpen) {
      unlockBodyScroll();
    }

    // Clear search and exit search mode
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    if (this.searchMode) {
      this.exitSearchMode();
    }
    this.currentMobileQuery = '';

    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.menuToggle);
    }
    this.updateZIndex();
  }

  switchTab(tab) {
    const tabId = tab.dataset.tab;
    const targetPanel = this.querySelector(`#tab-panel-${tabId}`);
    
    // Update tab states
    this.tabs.forEach(t => {
      const isActive = t === tab;
      t.classList.toggle('active', isActive);
      t.setAttribute('aria-selected', isActive);
      t.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    
    // Update panel states
    this.tabPanels.forEach(panel => {
      const isActive = panel === targetPanel;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
    });
  }

  // ==================
  // Search Functionality
  // ==================

  setupSearch() {
    if (!this.searchInput) return;

    // Additional search elements
    this.searchContent = this.querySelector('[data-search-content]');
    this.searchFocusState = this.querySelector('[data-mobile-search-focus]');
    this.searchKeywordState = this.querySelector('[data-mobile-search-keyword]');
    this.searchLoadingState = this.querySelector('[data-mobile-search-loading]');
    this.drawerTabs = this.querySelector('[data-drawer-tabs]');
    this.drawerPanels = this.querySelector('[data-drawer-panels]');
    this.drawerFooter = this.querySelector('[data-drawer-footer]');
    
    // Keyword state elements
    this.mobileProductsList = this.querySelector('[data-mobile-products-list]');
    this.mobileProductsSection = this.querySelector('[data-mobile-products-section]');
    this.mobileCollectionsList = this.querySelector('[data-mobile-collections-list]');
    this.mobileCollectionsSection = this.querySelector('[data-mobile-collections-section]');
    this.mobilePagesList = this.querySelector('[data-mobile-pages-list]');
    this.mobilePagesSection = this.querySelector('[data-mobile-pages-section]');
    this.mobileViewAll = this.querySelector('[data-mobile-view-all]');
    this.mobileViewAllLink = this.querySelector('[data-mobile-view-all-link]');
    this.mobileViewAllKeyword = this.querySelector('[data-mobile-view-all-keyword]');
    this.mobileNoResults = this.querySelector('[data-mobile-no-results]');
    
    // Search state
    this.searchMode = false;
    this.currentMobileQuery = '';
    this.mobileAbortController = null;
    this.searchCache = new Map();

    // Input handler
    this.searchInput.addEventListener('input', this.handleSearchInput);
    
    // Focus states
    this.searchInput.addEventListener('focus', () => {
      this.isSearchFocused = true;
      // Only enter search mode if not already in it (prevents redundant calls)
      if (!this.searchMode) {
        this.enterSearchMode();
      }
    });
    
    // Close button - two-step behavior
    if (this.searchClose) {
      this.searchClose.addEventListener('click', () => this.handleSearchClose());
    }
    
    // Suggestion tag clicks
    this.querySelectorAll('[data-mobile-suggestion]').forEach(tag => {
      tag.addEventListener('click', (e) => this.handleMobileSuggestionClick(e));
    });
    
    // Color filter clicks
    this.querySelectorAll('[data-mobile-color-filter]').forEach(swatch => {
      swatch.addEventListener('click', (e) => this.handleMobileColorClick(e));
    });
  }

  enterSearchMode() {
    if (this.searchMode) return;
    
    this.searchMode = true;
    this.setAttribute('data-search-mode', 'on');
    setAriaExpanded(document.getElementById('search-toggle-mobile'), true);
    
    // Hide drawer content (tabs, panels, footer) with fade transition
    if (this.drawerTabs) {
      this.drawerTabs.classList.add('nav-drawer__content--hidden');
      // Hide after transition completes
      setTimeout(() => {
        if (this.drawerTabs) this.drawerTabs.hidden = true;
      }, 200);
    }
    if (this.drawerPanels) {
      this.drawerPanels.classList.add('nav-drawer__content--hidden');
      setTimeout(() => {
        if (this.drawerPanels) this.drawerPanels.hidden = true;
      }, 200);
    }
    if (this.drawerFooter) {
      this.drawerFooter.classList.add('nav-drawer__content--hidden');
      setTimeout(() => {
        if (this.drawerFooter) this.drawerFooter.hidden = true;
      }, 200);
    }
    
    // Show search content with fade transition
    if (this.searchContent) {
      // Remove hidden attribute first to get element into layout flow
      this.searchContent.hidden = false;
      // Set element to invisible but in layout (opacity 0, visibility hidden)
      this.searchContent.classList.add('nav-drawer__content--hidden');
      this.searchContent.style.opacity = '0';
      this.searchContent.style.visibility = 'hidden';
      // Force a reflow to ensure styles are applied
      void this.searchContent.offsetHeight;
      // Force container reflow to ensure element is positioned correctly
      if (this.container) {
        void this.container.offsetHeight;
      }
      // Use requestAnimationFrame to ensure browser has rendered element in correct position
      requestAnimationFrame(() => {
        // Force another reflow to ensure element is rendered with opacity 0 in correct position
        void this.searchContent.offsetHeight;
        // Trigger transition after browser has rendered - use another RAF
        requestAnimationFrame(() => {
          if (this.searchContent) {
            // Remove class and inline styles to trigger transition to default (opacity 1)
            this.searchContent.classList.remove('nav-drawer__content--hidden');
            this.searchContent.style.opacity = '';
            this.searchContent.style.visibility = '';
            // Force reflow to ensure transition starts
            void this.searchContent.offsetHeight;
          }
        });
      });
    }
    
    // Show focus state if no query
    if (!this.searchInput?.value.trim()) {
      this.showMobileFocusState();
    }
    this.updateZIndex();
  }

  exitSearchMode() {
    this.searchMode = false;
    this.setAttribute('data-search-mode', 'off');
    setAriaExpanded(document.getElementById('search-toggle-mobile'), false);
    
    // Hide search content with fade transition
    if (this.searchContent) {
      // Add hidden class first to start fade out
      this.searchContent.classList.add('nav-drawer__content--hidden');
      // Remove from layout flow immediately (don't wait for transition)
      this.searchContent.hidden = true;
      // Force layout recalculation after removing search content
      if (this.container) {
        void this.container.offsetHeight;
      }
    }
    this.hideMobileAllStates();
    
    // Force layout recalculation of drawer container to prevent elements appearing at wrong position
    if (this.container) {
      void this.container.offsetHeight;
    }
    
    // Show drawer content with fade transition
    // Use inline styles for more reliable transition control
    const showWithFade = (element, name) => {
      if (!element) return;
      // Reset scroll position if element is scrollable (panels container)
      if (name === 'panels') {
        // Always reset scroll position to prevent content appearing at bottom
        element.scrollTop = 0;
      }
      // Remove hidden attribute FIRST to get element into layout flow
      element.hidden = false;
      // Reset scroll position (for panels container) - do this while element is in layout
      if (name === 'panels') {
        element.scrollTop = 0;
      }
      // Force drawer container layout recalculation to position element correctly
      if (this.container) {
        void this.container.offsetHeight;
      }
      // Force a reflow to ensure layout is calculated with element in flow
      void element.offsetHeight;
      // Now set element to invisible but in layout (opacity 0, visibility hidden)
      element.classList.add('nav-drawer__content--hidden');
      element.style.opacity = '0';
      element.style.visibility = 'hidden';
      // Force another reflow to ensure styles are applied
      void element.offsetHeight;
      // Force container reflow again to ensure element is positioned correctly
      if (this.container) {
        void this.container.offsetHeight;
      }
      // Use requestAnimationFrame to ensure browser has rendered element in correct position
      requestAnimationFrame(() => {
        // Force another reflow to ensure element is rendered with opacity 0 in correct position
        void element.offsetHeight;
        // Trigger transition after browser has rendered - use another RAF
        requestAnimationFrame(() => {
          if (element) {
            // Remove class and inline styles to trigger transition to default (opacity 1)
            element.classList.remove('nav-drawer__content--hidden');
            element.style.opacity = '';
            element.style.visibility = '';
            // Force reflow to ensure transition starts
            void element.offsetHeight;
          }
        });
      });
    };
    
    showWithFade(this.drawerTabs, 'tabs');
    showWithFade(this.drawerPanels, 'panels');
    showWithFade(this.drawerFooter, 'footer');
    
    // Cancel pending requests
    this.cancelMobileSearchRequests();
    this.updateZIndex();
  }

  showMobileFocusState() {
    this.hideMobileAllStates();
    if (this.searchFocusState) this.searchFocusState.hidden = false;
  }

  showMobileKeywordState() {
    this.hideMobileAllStates();
    if (this.searchKeywordState) this.searchKeywordState.hidden = false;
  }

  showMobileLoadingState() {
    if (this.searchLoadingState) this.searchLoadingState.hidden = false;
  }

  hideMobileLoadingState() {
    if (this.searchLoadingState) this.searchLoadingState.hidden = true;
  }

  hideMobileAllStates() {
    if (this.searchFocusState) this.searchFocusState.hidden = true;
    if (this.searchKeywordState) this.searchKeywordState.hidden = true;
    if (this.searchLoadingState) this.searchLoadingState.hidden = true;
  }

  handleSearchInput() {
    const query = this.searchInput?.value.trim();
    
    // Ensure we're in search mode
    this.enterSearchMode();
    
    if (!query) {
      this.currentMobileQuery = '';
      this.cancelMobileSearchRequests();
      this.showMobileFocusState();
      return;
    }
    
    this.currentMobileQuery = query;
    this.fetchMobileSearchResults(query);
  }

  async fetchMobileSearchResults(query) {
    // Check cache first
    const cached = this.getMobileSearchCache(query);
    if (cached) {
      this.renderMobileResults(cached, query);
      return;
    }

    // Cancel any pending requests
    this.cancelMobileSearchRequests();
    
    // Create new abort controller
    this.mobileAbortController = new AbortController();
    
    // Show loading
    this.showMobileLoadingState();

    try {
      const response = await fetch(
        `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=8&section_id=predictive-search`,
        { signal: this.mobileAbortController.signal }
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const html = await response.text();
      const doc = parseHTML(html);
      const results = doc.querySelector('#shopify-section-predictive-search');
      
      if (results) {
        const data = this.parseMobileResults(results);
        this.setMobileSearchCache(query, data);
        
        if (query === this.currentMobileQuery) {
          this.renderMobileResults(data, query);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Mobile search error:', error);
      this.hideMobileLoadingState();
      this.showMobileFocusState();
    }
  }

  parseMobileResults(container) {
    const data = { products: [], collections: [], pages: [] };
    
    // Parse products (using card-product structure)
    container.querySelectorAll('[data-product-result]').forEach(el => {
      const img = el.querySelector('img');
      const titleLink = el.querySelector('.card__heading a');
      const productLink = el.querySelector('.card-product__media');
      const priceContainer = el.querySelector('.price');
      const priceRegular = el.querySelector('.price-item--regular');
      const priceSale = el.querySelector('.price-item--sale');
      const comparePriceEl = el.querySelector('.price__sale s.price-item--regular');
      
      const url = productLink?.href || titleLink?.href || '';
      const title = titleLink?.textContent?.trim() || '';
      const variantLabel = el.querySelector('.card__variant')?.textContent?.trim() || '';
      const price = priceSale?.textContent?.trim() || priceRegular?.textContent?.trim() || '';
      const comparePrice = comparePriceEl?.textContent?.trim() || '';
      const onSale = priceContainer?.classList.contains('price--on-sale');
      
      data.products.push({
        url,
        title,
        variantLabel,
        image: img ? { src: img.src, srcset: img.srcset, alt: img.alt } : null,
        comparePrice,
        price,
        onSale
      });
    });
    
    container.querySelectorAll('[data-collection-result]').forEach(el => {
      const title = el.querySelector('.predictive-search-results__collection-title');
      const count = el.querySelector('.predictive-search-results__collection-count');
      
      data.collections.push({
        url: el.href,
        title: title?.textContent || '',
        count: count?.textContent || ''
      });
    });
    
    container.querySelectorAll('[data-page-result]').forEach(el => {
      const title = el.querySelector('.predictive-search-results__page-title');
      
      data.pages.push({
        url: el.href,
        title: title?.textContent || ''
      });
    });
    
    return data;
  }

  renderMobileResults(data, query) {
    this.hideMobileLoadingState();
    
    const hasProducts = data.products.length > 0;
    const hasCollections = data.collections.length > 0;
    const hasPages = data.pages.length > 0;
    const hasResults = hasProducts || hasCollections || hasPages;
    
    if (!hasResults) {
      if (this.mobileNoResults) this.mobileNoResults.hidden = false;
      this.showMobileKeywordState();
      return;
    }
    
    if (this.mobileNoResults) this.mobileNoResults.hidden = true;
    
    // Render products (using collection card structure)
    if (this.mobileProductsList) {
      this.mobileProductsList.innerHTML = data.products.map(product => `
        <div class="card-product__wrapper">
          <div class="card__image">
            <a href="${product.url}" class="card-product__media placeholder">
              ${product.image ? `
                <img
                  src="${product.image.src}"
                  srcset="${product.image.srcset}"
                  alt="${this.escapeHtml(product.image.alt)}"
                  class="motion-reduce"
                  loading="lazy"
                />
              ` : ''}
            </a>
          </div>
          <div class="card__content">
            <h3 class="card__heading h5">
              <a href="${product.url}" title="${this.escapeHtml(product.title)}">${this.escapeHtml(product.title)}</a>
            </h3>
            ${product.variantLabel ? `<div class="card__variant">${this.escapeHtml(product.variantLabel)}</div>` : ''}
            <div class="price${product.onSale && product.comparePrice && product.comparePrice !== product.price ? ' price--on-sale' : ''}">
              <div class="price__container">
                ${product.onSale && product.comparePrice && product.comparePrice !== product.price ? `
                  <div class="price__sale">
                    <span>
                      <s class="price-item price-item--regular">${product.comparePrice}</s>
                    </span>
                    <span class="visually-hidden visually-hidden--inline">Sale price</span>
                    <span class="price-item price-item--sale">${product.price}</span>
                  </div>
                ` : `
                  <div class="price__regular">
                    <span class="visually-hidden visually-hidden--inline">Regular price</span>
                    <span class="price-item price-item--regular">${product.price}</span>
                  </div>
                `}
              </div>
            </div>
          </div>
        </div>
      `).join('');
    }
    
    // Render collections
    if (this.mobileCollectionsList && this.mobileCollectionsSection) {
      if (hasCollections) {
        this.mobileCollectionsSection.hidden = false;
        this.mobileCollectionsList.innerHTML = data.collections.map(collection => `
          <li><a href="${collection.url}" class="nav-drawer__search-link">${this.escapeHtml(collection.title)} ${collection.count ? `<span class="nav-drawer__search-count">${collection.count}</span>` : ''}</a></li>
        `).join('');
      } else {
        this.mobileCollectionsSection.hidden = true;
      }
    }
    
    // Render pages
    if (this.mobilePagesList && this.mobilePagesSection) {
      if (hasPages) {
        this.mobilePagesSection.hidden = false;
        this.mobilePagesList.innerHTML = data.pages.map(page => `
          <li><a href="${page.url}" class="nav-drawer__search-link">${this.escapeHtml(page.title)}</a></li>
        `).join('');
      } else {
        this.mobilePagesSection.hidden = true;
      }
    }
    
    // Update view all button
    if (this.mobileViewAll && this.mobileViewAllLink && this.mobileViewAllKeyword) {
      this.mobileViewAll.hidden = false;
      this.mobileViewAllLink.href = `/search?q=${encodeURIComponent(query)}`;
      this.mobileViewAllKeyword.textContent = query;
    }
    
    this.showMobileKeywordState();
  }

  getMobileSearchCache(query) {
    const key = query.toLowerCase();
    if (this.searchCache.has(key)) {
      const { data, timestamp } = this.searchCache.get(key);
      if (Date.now() - timestamp < 5 * 60 * 1000) return data;
      this.searchCache.delete(key);
    }
    return null;
  }

  setMobileSearchCache(query, data) {
    const key = query.toLowerCase();
    if (this.searchCache.size >= 50) {
      const firstKey = this.searchCache.keys().next().value;
      this.searchCache.delete(firstKey);
    }
    this.searchCache.set(key, { data, timestamp: Date.now() });
  }

  cancelMobileSearchRequests() {
    if (this.mobileAbortController) {
      this.mobileAbortController.abort();
      this.mobileAbortController = null;
    }
  }

  handleMobileSuggestionClick(event) {
    event.preventDefault();
    const query = event.currentTarget.textContent.trim();
    if (this.searchInput) {
      this.searchInput.value = query;
    }
    this.currentMobileQuery = query;
    this.fetchMobileSearchResults(query);
  }

  handleMobileColorClick(event) {
    event.preventDefault();
    const color = event.currentTarget.dataset.mobileColorFilter;
    if (color) {
      window.location.href = `/search?q=${encodeURIComponent(color)}&type=product`;
    }
  }

  // Use global escapeHtml function from global.js
  escapeHtml(text) {
    return escapeHtml(text);
  }

  handleSearchClose() {
    // Single-action close: close search + drawer on first click.
    if (this.searchInput) this.searchInput.value = '';
    this.currentMobileQuery = '';
    this.cancelMobileSearchRequests();
    this.hideMobileAllStates();
    this.closeMobileMenu();
  }

  // ==================
  // Overlay & Z-Index Management
  // ==================

  handleOverlayClick() {
    // Skip if cart or size chart drawer is handling the overlay
    if (
      this.overlay?.classList.contains('cart-overlay') ||
      this.overlay?.classList.contains('size-chart-overlay') ||
      this.overlay?.classList.contains('wishlist-overlay') ||
      this.overlay?.classList.contains('currency-overlay') ||
      this.overlay?.classList.contains('search-overlay')
    ) {
      return;
    }
    
    if (this.activePanel) {
      this.closeAllPanels();
    }
    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }
  }

  setupOverlayMonitoring() {
    // Watch for cart, filters, quickview state changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        this.updateZIndex();
      });
    });

    // Monitor cart drawer
    const cartDrawer = document.querySelector('#cart-drawer, cart-drawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Monitor quickview modal
    const quickview = document.querySelector('#product-quick-view-modal, product-quick-view-modal');
    if (quickview) {
      observer.observe(quickview, { attributes: true, attributeFilter: ['class'] });
    }

    // Monitor filters
    const filters = document.querySelector('.facets-drawer, #facets-drawer');
    if (filters) {
      observer.observe(filters, { attributes: true, attributeFilter: ['class'] });
    }
    if (this.overlay) {
      observer.observe(this.overlay, { attributes: true, attributeFilter: ['class'] });
    }
  }

  isOverlayActive() {
    // Check if any overlay that should be above nav is active
    const cartDrawer = document.querySelector('#cart-drawer.active, cart-drawer.active');
    const quickview = document.querySelector('#product-quick-view-modal.is-open, product-quick-view-modal.is-open');
    const filters = document.querySelector('.facets-drawer.active, #facets-drawer.active');
    const filterPanelOpen = isMobileCollectionFilterDrawerOpen();
    // Treat the shared overlay as "owned by something else" only when it's tagged
    // for a non-nav drawer. The nav drawer activates the overlay itself, so checking
    // the bare `active` class would cause the nav to flag itself as behind an overlay
    // and apply pointer-events:none to its own header (breaking the burger toggle).
    const overlayOwnedByOther = this.overlay && (
      this.overlay.classList.contains('cart-overlay') ||
      this.overlay.classList.contains('wishlist-overlay') ||
      this.overlay.classList.contains('search-overlay') ||
      this.overlay.classList.contains('size-chart-overlay') ||
      this.overlay.classList.contains('currency-overlay')
    );

    return !!(cartDrawer || quickview || filters || filterPanelOpen || overlayOwnedByOther);
  }

  updateZIndex() {
    if (this.isOverlayActive()) {
      this.header?.classList.add('nav__behind-overlay');
      this.header?.classList.remove('nav__in-front');
    } else if (this.activePanel || this.isMobileMenuOpen()) {
      this.header?.classList.remove('nav__behind-overlay');
      this.header?.classList.add('nav__in-front');
    } else {
      this.header?.classList.remove('nav__behind-overlay', 'nav__in-front');
    }
  }

  // ==================
  // Transparent Mode Hover
  // ==================

  setupTransparentHover() {
    if (!this.header) return;
    
    // Bind handlers
    this.handleNavMouseEnter = this.handleNavMouseEnter.bind(this);
    this.handleNavMouseLeave = this.handleNavMouseLeave.bind(this);
    
    // Add hover listeners
    this.header.addEventListener('mouseenter', this.handleNavMouseEnter);
    this.header.addEventListener('mouseleave', this.handleNavMouseLeave);
  }

  handleNavMouseEnter() {
    if (!this.header) return;
    const isTransparent = this.header.dataset.transparent === 'true';
    if (isTransparent) {
      this.header.classList.add('nav__hovered');
    }
  }

  handleNavMouseLeave() {
    if (!this.header) return;
    this.header.classList.remove('nav__hovered');
  }

  // ==================
  // Keyboard Navigation
  // ==================

  handleKeydown(event) {
    const { key } = event;

    // Escape closes menus
    if (key === 'Escape') {
      if (this.activePanel) {
        this.closeAllPanels();
        this.activeTab?.focus();
        event.preventDefault();
      }
      if (this.isMobileMenuOpen()) {
        this.closeMobileMenu();
        this.menuToggle?.focus();
        event.preventDefault();
      }
      return;
    }

    // Arrow key navigation for desktop menu
    if (this.isDesktop && document.activeElement?.classList.contains('nav__menu-link')) {
      const links = Array.from(this.menuLinks);
      const currentIndex = links.indexOf(document.activeElement);
      
      if (currentIndex === -1) return;
      
      let newIndex = currentIndex;
      
      if (key === 'ArrowRight') {
        newIndex = (currentIndex + 1) % links.length;
        event.preventDefault();
      } else if (key === 'ArrowLeft') {
        newIndex = (currentIndex - 1 + links.length) % links.length;
        event.preventDefault();
      }
      
      if (newIndex !== currentIndex) {
        links[newIndex].focus();
      }
    }

    // Tab key navigation for mobile tabs
    if (!this.isDesktop && this.isMobileMenuOpen()) {
      const activeTab = this.querySelector('.nav-drawer__tab.active');
      if (document.activeElement?.classList.contains('nav-drawer__tab')) {
        const tabsArray = Array.from(this.tabs);
        const currentIndex = tabsArray.indexOf(document.activeElement);
        
        if (key === 'ArrowRight' || key === 'ArrowDown') {
          const nextIndex = (currentIndex + 1) % tabsArray.length;
          tabsArray[nextIndex].focus();
          this.switchTab(tabsArray[nextIndex]);
          event.preventDefault();
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          const prevIndex = (currentIndex - 1 + tabsArray.length) % tabsArray.length;
          tabsArray[prevIndex].focus();
          this.switchTab(tabsArray[prevIndex]);
          event.preventDefault();
        }
      }
    }
  }

  // ==================
  // Currency Selector
  // ==================

  setupCurrencySelectors() {
    const selectors = document.querySelectorAll('[data-currency-selector]');
    
    selectors.forEach(select => {
      select.addEventListener('change', async function() {
        const form = this.closest('form');
        if (!form) return;
        
        select.classList.add('loading');
        
        try {
          const formData = new FormData(form);
          const response = await fetch(form.action, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            window.location.reload();
          } else {
            console.error('Currency update failed');
            select.classList.remove('loading');
          }
        } catch (error) {
          console.error('Currency error:', error);
          select.classList.remove('loading');
          form.submit();
        }
      });
    });
  }
}

// ==================
// Currency Drawer
// ==================

class CurrencyDrawer extends HTMLElement {
  constructor() {
    super();
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.isUpdating = false;
    this.initialized = false;
    this._abortController = null;
    this._reloadTimeout = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    
    this.drawer = this.querySelector('#currency-drawer') || document.getElementById('currency-drawer');
    this.toggle = document.querySelector('[data-currency-trigger]');
    this.overlay = document.getElementById('overlay');
    this.currencyContainer = document.querySelector('.nav__currency');

    this.init();
    this.initialized = true;
  }

  init() {
    if (!this.drawer) {
      this.drawer = document.getElementById('currency-drawer');
    }

    if (!this.toggle) {
      this.toggle = document.querySelector('[data-currency-trigger]');
    }

    if (!this.drawer || !this.toggle) {
      setTimeout(() => {
        this.drawer = document.getElementById('currency-drawer');
        this.toggle = document.querySelector('[data-currency-trigger]');
        if (this.drawer && this.toggle) {
          this.init();
        }
      }, 100);
      return;
    }

    // Add .has-js class for progressive enhancement
    if (this.currencyContainer && !this.currencyContainer.classList.contains('has-js')) {
      this.currencyContainer.classList.add('has-js');
      
      // Remove inline display:none from drawer trigger (inline styles override CSS)
      const drawerTrigger = this.currencyContainer.querySelector('.nav__currency-drawer-trigger');
      if (drawerTrigger && drawerTrigger.style.display === 'none') {
        drawerTrigger.style.display = '';
      }
    }

    // Event listeners
    if (this.drawer) {
      this.drawer.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });
    }

    this.querySelectorAll('.currency-drawer__close').forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.close());
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.open();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (this.drawer && this.drawer.classList.contains('active')) {
          this.close();
        }
      });
    }

          // Currency item click handlers
          this.querySelectorAll('.currency-drawer__item').forEach((item) => {
            item.addEventListener('click', async (e) => {
              e.preventDefault();
              const currencyCode = item.dataset.currencyCode;
              if (currencyCode) {
                await this.selectCurrency(currencyCode, item);
              }
            });
          });
  }

  open() {
    if (!this.drawer) return;
    const wasActive = this.drawer.classList.contains('active');

    // Don't open drawer or reset loading state if we're currently updating/reloading
    if (this.isUpdating || this._reloadTimeout) {
      return; // Keep loading spinner visible, don't open drawer
    }

    // Reset updating flag and clear any pending operations
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    
    // Reset loading spinner state
    if (this.toggle) {
      this.toggle.classList.remove('loading');
    }
    
    // Re-enable all currency buttons
    this.querySelectorAll('.currency-drawer__item').forEach((item) => {
      item.disabled = false;
      item.style.pointerEvents = '';
      item.style.opacity = '';
    });

    // Close nav drawer if open (mobile)
    const nav = document.querySelector('nav-drawer');
    if (nav && typeof nav.closeMobileMenu === 'function') {
      nav.closeMobileMenu();
    }
    
    // Close desktop nav dropdown panels if open
    if (nav && typeof nav.closeAllPanels === 'function') {
      nav.closeAllPanels();
    }

    // Update toggle aria-expanded
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    // Show drawer
    this.drawer.classList.add('animate', 'active');
    this.drawer.style.zIndex = '2147483647';
    this.drawer.style.visibility = 'visible';

    if (this.overlay) {
      this.overlay.classList.add('active', 'currency-overlay');
    }

    if (!wasActive) {
      lockBodyScroll();
    }
    // Delay adding body click handler to prevent the opening click from triggering it
    setTimeout(() => {
      document.body.addEventListener('click', this.onBodyClick);
    }, 0);

    this.drawer.addEventListener('transitionend', () => {
      this.drawer.focus();
      if (typeof trapFocus === 'function') {
        trapFocus(this.drawer);
      }
    }, { once: true });
  }

  close() {
    if (!this.drawer) return;
    const wasActive = this.drawer.classList.contains('active');

    // Update toggle aria-expanded
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    this.drawer.classList.remove('active');

    if (this.overlay) {
      // Add fading class to trigger CSS fade while keeping top:0
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        // Now remove all drawer-related classes
        this.overlay.classList.remove('currency-overlay', 'overlay-fading');
      }, 300); // Match the overlay transition duration
    }

    if (wasActive) {
      unlockBodyScroll();
    }
    document.body.removeEventListener('click', this.onBodyClick);
  }

  handleBodyClick(event) {
    // Only handle clicks if drawer is actually active
    if (!this.drawer || !this.drawer.classList.contains('active')) {
      return;
    }
    
    // Close if clicking outside the drawer
    if (this.drawer && this.drawer.contains(event.target)) {
      return;
    }
    if (this.toggle && this.toggle.contains(event.target)) {
      return;
    }
    this.close();
  }

  async selectCurrency(currencyCode, itemElement) {
    // Prevent multiple clicks
    if (this.isUpdating) return;
    
    // Cancel any pending request
    if (this._abortController) {
      this._abortController.abort();
    }
    
    // Clear any pending reload timeout
    if (this._reloadTimeout) {
      clearTimeout(this._reloadTimeout);
      this._reloadTimeout = null;
    }
    
    this.isUpdating = true;
    
    // Disable all currency buttons immediately to prevent rapid clicks
    this.querySelectorAll('.currency-drawer__item').forEach((item) => {
      item.disabled = true;
      item.style.pointerEvents = 'none';
      item.style.opacity = '0.5';
    });
    
    const form = this.querySelector('.currency-drawer__form');
    
    if (!form) {
      this.isUpdating = false;
      // Re-enable buttons
      this.querySelectorAll('.currency-drawer__item').forEach((item) => {
        item.disabled = false;
        item.style.pointerEvents = '';
        item.style.opacity = '';
      });
      return;
    }

    // Update selected state visually
    this.querySelectorAll('.currency-drawer__item').forEach((item) => {
      item.classList.remove('currency-drawer__item--selected');
      const itemCheckbox = item.querySelector('.currency-drawer__checkbox');
      itemCheckbox.classList.remove('currency-drawer__checkbox--selected');
      // Remove SVG tick if it exists
      const existingSVG = itemCheckbox.querySelector('svg');
      if (existingSVG) {
        existingSVG.remove();
      }
    });
    
    itemElement.classList.add('currency-drawer__item--selected');
    const checkbox = itemElement.querySelector('.currency-drawer__checkbox');
    checkbox.classList.add('currency-drawer__checkbox--selected');
    
    // Create SVG tick if it doesn't exist
    if (!checkbox.querySelector('svg')) {
      const tickSVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tickSVG.setAttribute('viewBox', '0 0 12 12');
      tickSVG.setAttribute('width', '8');
      tickSVG.setAttribute('height', '8');
      tickSVG.setAttribute('fill', 'none');
      tickSVG.setAttribute('stroke', 'white');
      tickSVG.setAttribute('stroke-width', '2');
      tickSVG.setAttribute('stroke-linecap', 'round');
      tickSVG.setAttribute('stroke-linejoin', 'round');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M2 6l3 3 5-6');
      tickSVG.appendChild(path);
      checkbox.appendChild(tickSVG);
    }

    // Set currency code in form
    const currencyInput = form.querySelector('input[name="currency_code"]');
    if (currencyInput) {
      currencyInput.value = currencyCode;
    }

    // Update button text
    if (this.toggle) {
      const textElement = this.toggle.querySelector('.nav__currency-select-text');
      if (textElement) {
        textElement.textContent = currencyCode;
      }
      
      // Show spinner and hide chevron
      this.toggle.classList.add('loading');
    }

    // Close drawer immediately when currency is selected
    this.close();

    // Create new AbortController for this request
    this._abortController = new AbortController();

    try {
      const formData = new FormData(form);
      // Force a single authoritative currency value in payload.
      formData.set('currency_code', currencyCode);
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        signal: this._abortController.signal
      });
      
      // Check if request was aborted
      if (this._abortController.signal.aborted) {
        return;
      }
      
      if (response.ok) {
        // Store timeout so we can clear it if needed
        // Shopify requires page reload to fetch updated server-side prices
        this._reloadTimeout = setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        console.error('Currency update failed');
        this.isUpdating = false;
        // Re-enable buttons
        this.querySelectorAll('.currency-drawer__item').forEach((item) => {
          item.disabled = false;
          item.style.pointerEvents = '';
          item.style.opacity = '';
        });
        // Revert selection on error
        const previouslySelected = this.drawer.querySelector('.currency-drawer__item--selected');
        if (previouslySelected && previouslySelected !== itemElement) {
          itemElement.classList.remove('currency-drawer__item--selected');
          itemElement.querySelector('.currency-drawer__checkbox').classList.remove('currency-drawer__checkbox--selected');
          previouslySelected.classList.add('currency-drawer__item--selected');
          previouslySelected.querySelector('.currency-drawer__checkbox').classList.add('currency-drawer__checkbox--selected');
        }
      }
    } catch (error) {
      // Ignore abort errors (when user clicks another currency quickly)
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Currency error:', error);
      this.isUpdating = false;
      // Re-enable buttons
      this.querySelectorAll('.currency-drawer__item').forEach((item) => {
        item.disabled = false;
        item.style.pointerEvents = '';
        item.style.opacity = '';
      });
      // Fallback to form submission
      form.submit();
    }
  }
}

// Register custom element
customElements.define('currency-drawer', CurrencyDrawer);

// Register custom element
customElements.define('nav-drawer', NavDrawer);

// ==================
// Search Toggle (Desktop)
// ==================

document.addEventListener('DOMContentLoaded', () => {
  const searchToggle = document.getElementById('search-toggle');
  const searchToggleMobile = document.getElementById('search-toggle-mobile');
  const predictiveSearch = document.querySelector('predictive-search');
  const navDrawer = document.querySelector('nav-drawer');
  
  // Desktop search toggle
  if (searchToggle && predictiveSearch) {
    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      if (predictiveSearch.reveal) {
        predictiveSearch.reveal();
      }
    });
  }
  
  // Mobile search toggle - opens drawer and focuses search
  if (searchToggleMobile && navDrawer) {
    searchToggleMobile.addEventListener('click', (e) => {
      e.preventDefault();
      // Open the drawer
      navDrawer.openMobileMenu();
      // Enter search mode immediately to prevent flicker
      navDrawer.enterSearchMode();
      // Focus the search input after a short delay to allow transition
      setTimeout(() => {
        const searchInput = navDrawer.querySelector('[data-mobile-search-input]');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    });
  }
  
  // Cart toggle
  const cartToggle = document.getElementById('cart-toggle');
  if (cartToggle) {
    cartToggle.addEventListener('click', (e) => {
      e.preventDefault();
      if (typeof window.openCart === 'function') {
        window.openCart();
      }
    });
  }
});
